import { useState, useCallback } from 'react';

import { PAGINATION_META_DEFAULT_VALUE } from 'src/type/constants';
import { Pagination, PaginationMetaData } from 'src/type/utils';
import { omit } from 'lodash';

// ----------------------------------------------------------------------

type ReturnType<T> = {
  prevPage: number;
  currentPage: number;
  rowsPerPage: number;
  //
  onResetPage: VoidFunction;
  onChangePage: (event: unknown, newPage: number) => void;
  onChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  //
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  setRowsPerPage: React.Dispatch<React.SetStateAction<number>>;
  //
  data: T[];
  onSetData: (data: Pagination<T>) => void;
  meta: PaginationMetaData;
};

type Props = {
  defaultRowsPerPage?: number;
};

export default function usePagination<T>({ defaultRowsPerPage = 10 }: Props = {}): ReturnType<T> {
  const [currentPage, setCurrentPage] = useState(1);
  const [prevPage, setPrevPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage);
  const [data, setData] = useState(Array<T>());
  const [meta, setMeta] = useState<PaginationMetaData>(PAGINATION_META_DEFAULT_VALUE);

  const onChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentPage(1);
    setPrevPage(0);
    setData([]);
    setMeta(PAGINATION_META_DEFAULT_VALUE);
    setRowsPerPage(parseInt(event.target.value, 10));
  }, []);

  const onChangePage = useCallback((event: unknown, newPage: number) => {
    window.scrollTo(0, 0);
    setCurrentPage((prev) => {
      setPrevPage(prev);
      return newPage;
    });
  }, []);

  const onResetPage = useCallback(() => {
    setCurrentPage(1);
    setPrevPage(0);
    setData([]);
    setMeta(PAGINATION_META_DEFAULT_VALUE);
  }, []);

  const onSetData = useCallback((payload: Pagination<T>) => {
    setData(payload.results);
    setMeta(omit(payload, 'results'));
  }, []);

  return {
    prevPage,
    currentPage,
    rowsPerPage,
    //
    onChangePage,
    onResetPage,
    onChangeRowsPerPage,
    //
    setCurrentPage,
    setRowsPerPage,
    //
    data,
    onSetData,
    meta,
  };
}
